import React from 'react';
import { i18n } from 'config/locales';
import { Summary, NameSummary, DateSummary } from 'components/Summary';

import { FOREIGN, FOREIGN_BUSINESS_SPONSORSHIP } from 'config/formSections/foreign';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { foreignBusinessSponsorshipAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import SponsorshipItem from './SponsorshipItem';

const sectionConfig = {
  key: FOREIGN_BUSINESS_SPONSORSHIP.key,
  section: FOREIGN.name,
  store: FOREIGN.store,
  subsection: FOREIGN_BUSINESS_SPONSORSHIP.name,
  storeKey: FOREIGN_BUSINESS_SPONSORSHIP.storeKey,
};

export class Sponsorship extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      HasForeignSponsorship: this.props.HasForeignSponsorship,
      ...queue,
    });
  };

  updateHasForeignSponsorship = (values) => {
    this.update({
      HasForeignSponsorship: values,
      List: values.value === 'Yes' ? this.props.List : { items: [], branch: {} },
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  summary = (item, index) => {
    const obj = (item && item.Item) || {};
    const dates = DateSummary(obj.Dates);
    const name = NameSummary(obj.Name);

    return Summary({
      type: i18n.t('foreign.business.sponsorship.collection.summary.item'),
      index,
      left: name,
      right: dates,
      placeholder: i18n.t('foreign.business.sponsorship.collection.summary.unknown'),
    });
  };

  render() {
    const { errors, peopleWhoKnowYou, List } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, foreignBusinessSponsorshipAddressNameFields);
    const isPVQ = isPVQForm(this.props.formType);

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return isPVQ ? (
      <div
        className="section-content foreign-business-sponsorship"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_BUSINESS_SPONSORSHIP.key}
      >
        <h1 className="section-header">{i18n.t('foreign.pvq.subsection.business.sponsorship')}</h1>
        <Branch
          name="has_foreign_sponsorship"
          label={i18n.t('foreign.pvq.business.sponsorship.heading.title')}
          labelSize="h4"
          help="foreign.pvq.business.sponsorship.help.branch"
          {...this.props.HasForeignSponsorship}
          warning
          onUpdate={this.updateHasForeignSponsorship}
          required={this.props.required}
          onError={this.handleError}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasForeignSponsorship"
        />

        {this.props.HasForeignSponsorship.value === 'Yes'
          && (
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.pvq.business.sponsorship.collection.summary.title')}
            appendTitle={i18n.t('foreign.pvq.business.sponsorship.collection.appendTitle')}
            appendLabel={i18n.t('foreign.pvq.business.sponsorship.collection.append')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <SponsorshipItem
              applicantBirthdate={this.props.applicantBirthdate}
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
              formType={this.props.formType}
            />
          </Accordion>
          )}
      </div>
    ) : (
      <div
        className="section-content foreign-business-sponsorship"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_BUSINESS_SPONSORSHIP.key}
      >
        <h1 className="section-header">{i18n.t('foreign.subsection.business.sponsorship')}</h1>
        <Branch
          name="has_foreign_sponsorship"
          label={i18n.t('foreign.business.sponsorship.heading.title')}
          labelSize="h4"
          help="foreign.business.sponsorship.help.branch"
          {...this.props.HasForeignSponsorship}
          warning
          onUpdate={this.updateHasForeignSponsorship}
          required={this.props.required}
          onError={this.handleError}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasForeignSponsorship"
        />

        <Show when={this.props.HasForeignSponsorship.value === 'Yes'}>
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.business.sponsorship.collection.summary.title')}
            appendTitle={i18n.t('foreign.business.sponsorship.collection.appendTitle')}
            appendLabel={i18n.t('foreign.business.sponsorship.collection.append')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <SponsorshipItem
              applicantBirthdate={this.props.applicantBirthdate}
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
            />
          </Accordion>
        </Show>
      </div>
    );
  }
}

Sponsorship.defaultProps = {
  name: 'Sponsorship',
  HasForeignSponsorship: {},
  List: {},
  onUpdate: () => {},
  onError: (value, arr) => arr,
  section: 'foreign',
  subsection: 'business/sponsorship',
  addressBooks: {},
  dispatch: () => {},
  defaultState: true,
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(Sponsorship, sectionConfig);
